import React, { forwardRef } from 'react';
import { ReactThreeFiber } from 'react-three-fiber';
import { Text } from 'drei';
import { PUBLIC_URL_ROOT } from '../../../config/secrets';
import { isIOS } from '../../../common/device';

const fontWeblysleek = `${PUBLIC_URL_ROOT}assets/fonts/weblysleek/weblysleekui_normal.ttf`;
const fontRoboto = `${PUBLIC_URL_ROOT}assets/fonts/roboto/roboto.regular.ttf`;

const fontFamily = isIOS() ? fontRoboto : fontWeblysleek;

type TTextProps = {
  value: string;
  highlightValue?: string;
};

const TitleText = forwardRef(({ value, highlightValue }: TTextProps, ref) => {
  const fontSize = 0.05;
  const textAlign = 'center';
  const anchorX = 'center';
  const anchorY = 'middle';
  const color = '#ffffff';
  const position: ReactThreeFiber.Vector3 = [0, 0, 0.001];

  return (
    <group position={position}>
      <Text
        renderOrder={2}
        color={color}
        font={fontFamily}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlign}
        anchorX={anchorX}
        anchorY={anchorY}>
        {value}
      </Text>
      <Text
        ref={ref}
        renderOrder={3}
        color="#ffffff"
        font={fontFamily}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlign}
        anchorX={anchorX}
        anchorY={anchorY}>
        {highlightValue}
      </Text>
    </group>
  );
});

const BigText = forwardRef(({ value, highlightValue }: TTextProps, ref) => {
  const fontSize = 0.028;
  const color = '#cccccc';
  const textAlign = 'left';
  const anchorX = 'left';
  const anchorY = 'top';
  const position: ReactThreeFiber.Vector3 = [-0.24, 0.2, 0.001];

  return (
    <group position={position}>
      <Text
        renderOrder={2}
        color={color}
        font={fontFamily}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlign}
        anchorX={anchorX}
        anchorY={anchorY}>
        {value}
      </Text>
      <Text
        ref={ref}
        renderOrder={3}
        color="#ffffff"
        font={fontFamily}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlign}
        anchorX={anchorX}
        anchorY={anchorY}>
        {highlightValue}
      </Text>
    </group>
  );
});

const DefaultText = forwardRef(({ value, highlightValue }: TTextProps, ref) => {
  const fontSize = 0.02;
  const color = '#cccccc';
  const textAlign = 'left';
  const anchorX = 'left';
  const anchorY = 'top';
  const position: ReactThreeFiber.Vector3 = [-0.24, -0.1, 0.001];

  return (
    <group position={position}>
      <Text
        renderOrder={2}
        color={color}
        font={fontFamily}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlign}
        anchorX={anchorX}
        anchorY={anchorY}>
        {value}
      </Text>
      <Text
        ref={ref}
        renderOrder={3}
        color="#ffffff"
        font={fontFamily}
        fontSize={fontSize}
        maxWidth={0.48}
        lineHeight={1.2}
        textAlign={textAlign}
        anchorX={anchorX}
        anchorY={anchorY}>
        {highlightValue}
      </Text>
    </group>
  );
});

type TStoryTextProps = TTextProps & {
  isBig?: boolean;
  isTitle?: boolean;
};

const StoryText = forwardRef(({ value, highlightValue = '', isTitle, isBig }: TStoryTextProps, ref) => {
  let Component = DefaultText;

  if (isTitle) {
    Component = TitleText;
  } else if (isBig) {
    Component = BigText;
  }

  return <Component value={value} highlightValue={highlightValue} ref={ref} />;
});

export default StoryText;
